import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { crApi } from "../lib/crScope";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { SearchableSelect } from "./ui/searchable-select";
import { Label } from "./ui/label";
import {
  Trash2,
  FileSpreadsheet,
  ChevronLeft,
  AlertTriangle,
  Loader2,
  Eye,
  Calendar,
  User,
  Hash,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteImportDataModal({ open, onOpenChange, getAuthHeaders, onDeleted }) {
  const [step, setStep] = useState("uploaders"); // uploaders | batches | preview
  const [uploaders, setUploaders] = useState([]);
  const [selectedUploader, setSelectedUploader] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("uploaders");
      setSelectedUploader(null);
      setSelectedBatch(null);
      setPreviewData(null);
      fetchUploaders();
    }
  }, [open]);

  const fetchUploaders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${crApi()}/control-renovaciones/import/uploaders`, getAuthHeaders());
      setUploaders(res.data);
    } catch (err) {
      toast.error("Error cargando lista de supervisores");
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async (uploader) => {
    setSelectedUploader(uploader);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}${crApi()}/control-renovaciones/import/batches?uploaded_by=${uploader.user_id}`,
        getAuthHeaders()
      );
      setBatches(res.data);
      setStep("batches");
    } catch (err) {
      toast.error("Error cargando archivos importados");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (batch) => {
    setSelectedBatch(batch);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}${crApi()}/control-renovaciones/import/batches/${batch.id}/preview`,
        getAuthHeaders()
      );
      setPreviewData(res.data);
      setStep("preview");
    } catch (err) {
      toast.error("Error cargando vista previa");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}${crApi()}/control-renovaciones/import/batches/${selectedBatch.id}`,
        getAuthHeaders()
      );
      toast.success(res.data.message);
      setConfirmOpen(false);
      onOpenChange(false);
      if (onDeleted) onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando data");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Fecha no disponible";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-DO", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="delete-import-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Eliminar Data Importada
            </DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 text-xs text-slate-500 border-b pb-2">
            <span className={step === "uploaders" ? "font-bold text-slate-800" : "cursor-pointer hover:text-slate-700"} onClick={() => { setStep("uploaders"); setSelectedUploader(null); setSelectedBatch(null); setPreviewData(null); }}>
              1. Supervisor
            </span>
            <ChevronLeft className="w-3 h-3 rotate-180" />
            <span className={step === "batches" ? "font-bold text-slate-800" : step === "preview" ? "cursor-pointer hover:text-slate-700" : "text-slate-400"} onClick={() => { if (selectedUploader) { setStep("batches"); setSelectedBatch(null); setPreviewData(null); } }}>
              2. Archivos
            </span>
            <ChevronLeft className="w-3 h-3 rotate-180" />
            <span className={step === "preview" ? "font-bold text-slate-800" : "text-slate-400"}>
              3. Vista Previa
            </span>
          </div>

          <div className="flex-1 overflow-auto">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Cargando...</span>
              </div>
            )}

            {/* STEP 1: Select Uploader */}
            {!loading && step === "uploaders" && (
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Seleccione el supervisor o usuario que importó la data:</Label>
                {uploaders.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No se encontraron importaciones registradas.</p>
                ) : (
                  <div className="grid gap-2 mt-2">
                    {uploaders.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => fetchBatches(u)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                        data-testid={`uploader-${u.user_id}`}
                      >
                        <User className="w-5 h-5 text-slate-400" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-slate-500">
                            {u.batch_count > 0 ? `${u.batch_count} importación(es) rastreada(s)` : ""}
                            {u.has_legacy ? " + importaciones anteriores" : ""}
                          </p>
                        </div>
                        <ChevronLeft className="w-4 h-4 rotate-180 text-slate-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Select Batch/File */}
            {!loading && step === "batches" && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Button variant="ghost" size="sm" onClick={() => { setStep("uploaders"); setSelectedUploader(null); }}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Volver
                  </Button>
                  <span className="text-sm text-slate-600">Archivos importados por <strong>{selectedUploader?.name}</strong>:</span>
                </div>
                {batches.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No se encontraron importaciones para este usuario.</p>
                ) : (
                  <div className="grid gap-2">
                    {batches.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => fetchPreview(b)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-orange-400 hover:bg-orange-50 transition-colors text-left"
                        data-testid={`batch-${b.id}`}
                      >
                        <FileSpreadsheet className="w-5 h-5 text-green-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{b.filename || "Archivo sin nombre"}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(b.uploaded_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {b.current_record_count} registros
                            </span>
                          </div>
                        </div>
                        <Eye className="w-4 h-4 text-slate-400 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3: Preview + Delete */}
            {!loading && step === "preview" && previewData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setStep("batches"); setSelectedBatch(null); setPreviewData(null); }}>
                      <ChevronLeft className="w-4 h-4 mr-1" /> Volver
                    </Button>
                    <div>
                      <p className="text-sm font-medium">{selectedBatch?.filename}</p>
                      <p className="text-xs text-slate-500">{previewData.total} registros &middot; {formatDate(selectedBatch?.uploaded_at)}</p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    data-testid="delete-batch-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Procesar Eliminación
                  </Button>
                </div>
                <div className="border rounded-lg overflow-auto max-h-[50vh]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs w-10">#</TableHead>
                        <TableHead className="text-xs">Cliente</TableHead>
                        <TableHead className="text-xs">Póliza</TableHead>
                        <TableHead className="text-xs">Compañía</TableHead>
                        <TableHead className="text-xs">Ramo</TableHead>
                        <TableHead className="text-xs">Ejecutivo</TableHead>
                        <TableHead className="text-xs">Sucursal</TableHead>
                        <TableHead className="text-xs">Prima</TableHead>
                        <TableHead className="text-xs">Vence</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.records.map((r, i) => (
                        <TableRow key={r.id} className="text-xs">
                          <TableCell className="text-slate-400">{i + 1}</TableCell>
                          <TableCell className="font-medium max-w-[150px] truncate">{r.cliente}</TableCell>
                          <TableCell>{r.poliza}</TableCell>
                          <TableCell>{r.compania}</TableCell>
                          <TableCell>{r.ramo}</TableCell>
                          <TableCell>{r.ejecutivo}</TableCell>
                          <TableCell>{r.sucursal}</TableCell>
                          <TableCell>{r.moneda} {r.prima?.toLocaleString()}</TableCell>
                          <TableCell>{r.vence_en}</TableCell>
                          <TableCell>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${r.status === "PENDIENTE" ? "bg-yellow-100 text-yellow-800" : r.status === "RENOVADO" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`}>
                              {r.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Eliminación
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                Está a punto de eliminar <strong>{previewData?.total || 0} registros</strong> del archivo:
              </span>
              <span className="block font-medium text-slate-700 mb-2">
                "{selectedBatch?.filename}"
              </span>
              <span className="block text-red-600 font-medium">
                Esta acción es irreversible. ¿Desea continuar?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-batch"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Sí, Eliminar Todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
